import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Minus, RefreshCw, Lock, ExternalLink,
  Pencil, Plus, Music, Sparkles, ChevronDown, ChevronUp, X, Info, Globe,
} from "lucide-react";
import useCollapsible from "@/hooks/useCollapsible";

/**
 * Top Charts — Update #176
 *
 * Weekly Top Charts view per station. Blends sanctioned sources
 * (Apple Music + Last.fm) into one ranked list per region. Locked
 * sources (OCC, Billboard) render as permanent "needs licensing"
 * placeholders — no scraper wiring, ever.
 *
 * Design tokens follow the existing dashboard palette:
 *   bg-base #0f1117, card #1a1e2a, border #262b3a
 *   accent #4a9eff / tint #4a9eff1a
 *   status green #3ddc97, amber #f5a623, red #ef5a6f
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Auth helper ─────────────────────────────────────────────────────
function _auth() {
  const token =
    localStorage.getItem("session_token") ||
    document.cookie.split("session_token=")[1]?.split(";")[0];
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Design tokens ───────────────────────────────────────────────────
const T = {
  bg:         "#0f1117",
  card:       "#1a1e2a",
  cardHover:  "#20242f",
  border:     "#262b3a",
  divider:    "#1f2330",
  accent:     "#4a9eff",
  accentTint: "#4a9eff1a",
  accentLine: "#4a9eff40",
  up:         "#3ddc97",
  lock:       "#f5a623",
  lockTint:   "#f5a6231a",
  down:       "#ef5a6f",
  text:       "#e6e8ee",
  muted:      "#8b93a7",
  faint:      "#565d72",
};

// ── Page ────────────────────────────────────────────────────────────
export default function TopCharts() {
  const [regions,         setRegions]         = useState([]);
  const [sources,         setSources]         = useState([]);
  const [settings,        setSettings]        = useState(null);
  const [view,            setView]            = useState("blended");   // blended | applemusic | lastfm
  const [entries,         setEntries]         = useState([]);
  const [meta,            setMeta]            = useState({});
  const [loadingChart,    setLoadingChart]    = useState(false);
  const [refreshing,      setRefreshing]      = useState(false);
  const [warnings,        setWarnings]        = useState([]);
  const [addingRegion,    setAddingRegion]    = useState(false);
  const [savingSettings,  setSavingSettings]  = useState(false);
  const [heatmap,         setHeatmap]         = useState(null);
  const [heatmapLoading,  setHeatmapLoading]  = useState(false);

  // Bootstrap regions + sources + settings on mount
  useEffect(() => {
    Promise.all([
      fetch(`${API}/charts/regions`).then((r) => r.json()),
      fetch(`${API}/charts/sources`).then((r) => r.json()),
      fetch(`${API}/charts/settings`, { headers: _auth() }).then((r) => r.json()),
    ])
      .then(([regs, srcs, sets]) => {
        setRegions(regs.regions || []);
        setSources(srcs.sources || []);
        setSettings(sets.settings || null);
      })
      .catch(() => toast.error("Could not load chart configuration."));
  }, []);

  // Whenever primary region or view changes, load the chart
  const loadChart = useCallback(async () => {
    if (!settings?.primary_region) return;
    setLoadingChart(true);
    try {
      const r = await fetch(
        `${API}/charts/entries?region=${settings.primary_region}&view=${view}`,
        { headers: _auth() }
      );
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "load failed");
      setEntries(d.entries || []);
      setMeta({
        week_of:     d.week_of,
        week_end:    d.week_end,
        region_name: d.region_name,
      });
      setWarnings(d.warnings || []);
    } catch (e) {
      toast.error("Could not load chart data.");
    } finally {
      setLoadingChart(false);
    }
  }, [settings?.primary_region, view]);

  useEffect(() => { loadChart(); }, [loadChart]);

  // Load heatmap whenever the set of active_regions changes
  const activeRegionsKey = (settings?.active_regions || []).join(",");
  useEffect(() => {
    if (!activeRegionsKey) return;
    let cancelled = false;
    setHeatmapLoading(true);
    fetch(`${API}/charts/heatmap`, { headers: _auth() })
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setHeatmap(d); })
      .catch(() => { /* silent — tile just hides */ })
      .finally(() => { if (!cancelled) setHeatmapLoading(false); });
    return () => { cancelled = true; };
  }, [activeRegionsKey]);

  // ── Handlers ─────────────────────────────────────────────────────
  const patchSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const r = await fetch(`${API}/charts/settings`, {
        method: "PATCH",
        headers: { ..._auth(), "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "save failed");
      setSettings(d.settings);
      return d.settings;
    } catch (e) {
      toast.error(typeof e.message === "string" ? e.message : "Could not update settings.");
      return null;
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleSource = async (id) => {
    const src = sources.find((s) => s.id === id);
    if (!src || src.locked) {
      toast.info("This source needs a data-licensing agreement before we can enable it.");
      return;
    }
    const now = new Set(settings?.enabled_sources || []);
    if (now.has(id)) now.delete(id);
    else             now.add(id);
    await patchSettings({ enabled_sources: [...now] });
    // Force a chart refresh since source mix changed
    loadChart();
  };

  const setPrimaryRegion = async (code) => {
    if (code === settings?.primary_region) return;
    // If code isn't in active_regions, add it.
    const active = new Set(settings?.active_regions || []);
    active.add(code);
    await patchSettings({
      primary_region:  code,
      active_regions:  [...active],
    });
  };

  const addRegion = async (code) => {
    const active = new Set(settings?.active_regions || []);
    if (active.has(code)) {
      setPrimaryRegion(code);
    } else {
      active.add(code);
      await patchSettings({ active_regions: [...active], primary_region: code });
    }
    setAddingRegion(false);
  };

  const removeRegion = async (code) => {
    if (!settings) return;
    if ((settings.active_regions || []).length <= 1) {
      toast.info("Keep at least one region — try switching to another first.");
      return;
    }
    const active = (settings.active_regions || []).filter((c) => c !== code);
    const patch  = { active_regions: active };
    if (settings.primary_region === code) patch.primary_region = active[0];
    await patchSettings(patch);
  };

  const forceRefresh = async () => {
    if (!settings?.primary_region) return;
    setRefreshing(true);
    try {
      const r = await fetch(
        `${API}/charts/refresh?region=${settings.primary_region}`,
        { method: "POST", headers: _auth() }
      );
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "refresh failed");
      toast.success(`Refreshed — ${d.fetched} entries fetched`);
      loadChart();
    } catch (e) {
      toast.error("Could not refresh chart data.");
    } finally {
      setRefreshing(false);
    }
  };

  const sendToShowPrep = async (entry) => {
    if (!entry.entry_id) {
      toast.error("This row is out of date — reloading chart data…");
      loadChart();
      return;
    }
    try {
      const r = await fetch(
        `${API}/charts/entries/${entry.entry_id}/send-to-showprep`,
        {
          method: "POST",
          headers: { ..._auth(), "Content-Type": "application/json" },
          body:    JSON.stringify({}),
        }
      );
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "send failed");
      toast.success(`Sent "${entry.title}" to Show Prep`);
    } catch {
      toast.error("Could not push to Show Prep.");
    }
  };

  const availableRegionsToAdd = useMemo(
    () => (regions || []).filter(
      (r) => !((settings?.active_regions) || []).includes(r.code)
    ),
    [regions, settings]
  );

  return (
    <div className="min-h-screen px-6 py-6" style={{ background: T.bg, color: T.text }}
         data-testid="top-charts-page">
      <Header
        meta={meta}
        primaryRegion={settings?.primary_region}
        regionName={meta.region_name}
        onRefresh={forceRefresh}
        refreshing={refreshing}
      />

      <div className="grid gap-5 mt-5" style={{ gridTemplateColumns: "340px 1fr" }}>
        <div className="flex flex-col gap-4">
          <RegionCard
            regions={regions}
            activeRegions={settings?.active_regions || []}
            primaryRegion={settings?.primary_region}
            onSelect={setPrimaryRegion}
            onRemove={removeRegion}
            onAdd={() => setAddingRegion(true)}
          />
          <HeatmapCard
            heatmap={heatmap}
            loading={heatmapLoading}
            primaryRegion={settings?.primary_region}
            onSelectRegion={setPrimaryRegion}
          />
          <SourcesCard sources={sources.filter((s) => !s.is_premium)}
                       enabled={settings?.enabled_sources || []}
                       onToggle={toggleSource}
                       saving={savingSettings} />
          <PremiumDataCard sources={sources.filter((s) => s.is_premium)} />
          <ComplianceBanner />
        </div>

        <div className="flex flex-col">
          <ViewTabs view={view} setView={setView}
                    enabled={settings?.enabled_sources || []} />
          {warnings.length > 0 && <WarningStack warnings={warnings} />}
          <ChartList
            entries={entries}
            view={view}
            loading={loadingChart}
            onSendToShowPrep={sendToShowPrep}
          />
        </div>
      </div>

      {addingRegion && (
        <AddRegionModal
          available={availableRegionsToAdd}
          onPick={addRegion}
          onClose={() => setAddingRegion(false)}
        />
      )}
    </div>
  );
}

// ── Header ──────────────────────────────────────────────────────────
const Header = ({ meta, regionName, onRefresh, refreshing }) => (
  <div className="flex items-start justify-between gap-4 flex-wrap"
       data-testid="top-charts-header">
    <div>
      <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-3"
          style={{ color: T.text }}>
        <TrendingUp className="h-6 w-6" style={{ color: T.accent }} />
        Top Charts
      </h1>
      <p className="text-sm mt-1" style={{ color: T.muted }}>
        Weekly show-prep charts, blended across sanctioned data sources.
      </p>
    </div>
    <div className="flex items-center gap-3 flex-wrap">
      {meta.week_of && (
        <div className="px-3 py-1.5 rounded-lg border text-xs"
             style={{ borderColor: T.border, color: T.muted, background: T.card }}
             data-testid="week-label">
          Week of <span style={{ color: T.text, fontWeight: 600 }}>{meta.week_of} – {meta.week_end}</span>
          {regionName && <> · <span style={{ color: T.text }}>{regionName}</span></>}
        </div>
      )}
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        data-testid="top-charts-refresh"
        className="px-3 py-1.5 rounded-lg text-xs font-medium inline-flex items-center gap-2 transition disabled:opacity-50"
        style={{ borderColor: T.border, border: `1px solid ${T.border}`, color: T.text, background: T.card }}
      >
        <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
        Refresh
      </button>
    </div>
  </div>
);

// ── Collapsible card scaffolding ────────────────────────────────────
// Shared collapse/expand logic for every left-column card. Each card
// passes its own localStorage key so preferences persist across
// navigations. The header is always clickable, chevron flips on
// toggle, and cards render an optional `collapsedSummary` line so
// users still get a one-glance signal without expanding.
// useCollapsible is imported from '@/hooks/useCollapsible' — see top of file.
// The local implementation was moved out on Update #181 so News
// Ingestion could reuse the same behaviour.



const CollapsibleHeader = ({ icon: Icon, title, subline, collapsed, onToggle, testid, rightAction }) => (
  <div className="flex items-start gap-2">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!collapsed}
      data-testid={testid}
      className="flex-1 flex items-start gap-2 text-left transition"
      style={{ background: "transparent" }}
    >
      {Icon && <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: T.accent }} />}
      <div className="flex-1 min-w-0">
        <h2 className="text-sm font-semibold" style={{ color: T.text }}>{title}</h2>
        {subline && <div className="text-xs mt-0.5" style={{ color: T.muted }}>{subline}</div>}
      </div>
      <div className="rounded-md p-1 transition flex-shrink-0"
           style={{ color: T.muted, background: "transparent" }}>
        {collapsed
          ? <ChevronDown className="h-3.5 w-3.5" />
          : <ChevronUp   className="h-3.5 w-3.5" />}
      </div>
    </button>
    {rightAction && <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>{rightAction}</div>}
  </div>
);

// ── Region card ─────────────────────────────────────────────────────
const RegionCard = ({ regions, activeRegions, primaryRegion, onSelect, onRemove, onAdd }) => {
  const [collapsed, toggle] = useCollapsible("topcharts.region.collapsed");
  const active = (activeRegions || []).map((c) => regions.find((r) => r.code === c)).filter(Boolean);
  const primary = active.find((r) => r.code === primaryRegion);
  const summary = collapsed
    ? (primary ? `${primary.flag} ${primary.name} · ${active.length} active` : `${active.length} region${active.length === 1 ? "" : "s"}`)
    : "Chart data is filtered to your selected market";
  return (
    <div className="rounded-xl p-4" style={{ background: T.card, border: `1px solid ${T.border}` }}
         data-testid="region-card">
      <CollapsibleHeader
        title="Region"
        subline={summary}
        collapsed={collapsed}
        onToggle={toggle}
        testid="region-toggle"
        rightAction={!collapsed && (
          <button onClick={onAdd} data-testid="region-add-btn"
                  className="text-xs inline-flex items-center gap-1 rounded-md px-2 py-1 transition"
                  style={{ color: T.accent, background: T.accentTint }}>
            <Plus className="h-3 w-3" /> Add
          </button>
        )}
      />
      {!collapsed && (
        <div className="mt-3 flex flex-wrap gap-2">
          {active.map((r) => {
            const isPrimary = r.code === primaryRegion;
            return (
              <div key={r.code}
                   data-testid={`region-chip-${r.code}`}
                   className="group inline-flex items-center gap-1.5 rounded-full text-xs font-medium transition cursor-pointer"
                   style={{
                     padding: "6px 4px 6px 10px",
                     background: isPrimary ? T.accentTint : T.bg,
                     color:      isPrimary ? T.accent    : T.muted,
                     border:     `1px solid ${isPrimary ? T.accentLine : T.border}`,
                   }}
                   onClick={() => onSelect(r.code)}>
                <span style={{ fontSize: 13 }}>{r.flag}</span>
                <span>{r.name}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); onRemove(r.code); }}
                  data-testid={`region-remove-${r.code}`}
                  className="opacity-0 group-hover:opacity-70 hover:opacity-100 transition ml-0.5 mr-1"
                  style={{ color: T.faint }}
                  aria-label={`Remove ${r.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Region consensus heatmap tile ───────────────────────────────────
const HeatmapCard = ({ heatmap, loading, primaryRegion, onSelectRegion }) => {
  const [collapsed, toggle] = useCollapsible("topcharts.heatmap.collapsed");
  const rows = heatmap?.regions || [];
  if (!heatmap && !loading) return null;

  const subline = collapsed && rows.length > 0
    ? `${rows.length} market${rows.length === 1 ? "" : "s"} · top ${rows[0]?.consensus || 0}% ${rows[0]?.flag}`
    : "How much your 3 sources agree, per market";

  return (
    <div className="rounded-xl p-4" style={{ background: T.card, border: `1px solid ${T.border}` }}
         data-testid="heatmap-card">
      <CollapsibleHeader
        icon={Globe}
        title="Region consensus"
        subline={subline}
        collapsed={collapsed}
        onToggle={toggle}
        testid="heatmap-toggle"
      />

      {!collapsed && (
        <div className="mt-3">
          {loading && rows.length === 0 ? (
            <div className="text-xs" style={{ color: T.muted }}>Analysing markets…</div>
          ) : rows.length === 0 ? (
            <div className="text-xs" style={{ color: T.muted }}>Add another region to see the heatmap.</div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {rows.map((row) => {
                const isPrimary = row.code === primaryRegion;
                return (
                  <button
                    key={row.code}
                    type="button"
                    onClick={() => onSelectRegion(row.code)}
                    data-testid={`heatmap-row-${row.code}`}
                    className="w-full text-left rounded-lg p-2 transition"
                    style={{
                      background: isPrimary ? T.accentTint : T.bg,
                      border: `1px solid ${isPrimary ? T.accentLine : T.border}`,
                    }}
                    onMouseEnter={(e) => { if (!isPrimary) e.currentTarget.style.background = T.cardHover; }}
                    onMouseLeave={(e) => { if (!isPrimary) e.currentTarget.style.background = T.bg; }}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span style={{ fontSize: 14 }}>{row.flag}</span>
                      <span className="text-sm font-medium flex-1 truncate" style={{ color: T.text }}>
                        {row.name}
                      </span>
                      <ConsensusBadge value={row.consensus} />
                    </div>
                    <StrengthBar triple={row.triple} double={row.double}
                                 single={row.single} total={row.total} />
                    <div className="flex justify-between mt-1.5">
                      <div className="text-[10px]" style={{ color: T.faint, lineHeight: 1.3 }}>
                        {row.triple > 0
                          ? <><strong style={{ color: T.up }}>{row.triple}</strong> triple · </>
                          : null}
                        <strong style={{ color: T.text }}>{row.double}</strong> double · {row.single} single
                      </div>
                      {row.top_hit && (
                        <div className="text-[10px] truncate max-w-[55%]" style={{ color: T.muted }}
                             title={`${row.top_hit.title} — ${row.top_hit.artist}`}>
                          ↗ {row.top_hit.title}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Colour-graded consensus badge — 0-100 %.
const ConsensusBadge = ({ value }) => {
  const v = Math.max(0, Math.min(100, value || 0));
  const color = v >= 40 ? T.up : v >= 20 ? T.accent : v >= 10 ? T.lock : T.faint;
  const bg    = v >= 40 ? "rgba(61,220,151,0.14)"
              : v >= 20 ? T.accentTint
              : v >= 10 ? T.lockTint
              : "rgba(86,93,114,0.12)";
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
          style={{ background: bg, color, letterSpacing: "0.04em" }}>
      {v}%
    </span>
  );
};

// Stacked strength bar: triple (green) | double (accent) | single (faint)
const StrengthBar = ({ triple, double, single, total }) => {
  if (!total) return null;
  const pctTriple = (triple / total) * 100;
  const pctDouble = (double / total) * 100;
  const pctSingle = (single / total) * 100;
  return (
    <div className="flex overflow-hidden rounded-full" style={{ height: 5, background: T.divider }}>
      {pctTriple > 0 && <div style={{ width: `${pctTriple}%`, background: T.up }} />}
      {pctDouble > 0 && <div style={{ width: `${pctDouble}%`, background: T.accent }} />}
      {pctSingle > 0 && <div style={{ width: `${pctSingle}%`, background: T.faint, opacity: 0.6 }} />}
    </div>
  );
};

// ── Sources card ────────────────────────────────────────────────────
const SourcesCard = ({ sources, enabled, onToggle, saving }) => {
  const [collapsed, toggle] = useCollapsible("topcharts.sources.collapsed");
  const activeCount = (sources || []).filter((s) => (enabled || []).includes(s.id)).length;
  const summary = collapsed
    ? `${activeCount} of ${sources.length} active`
    : "Combine sanctioned sources into one weekly ranking";
  return (
    <div className="rounded-xl p-4" style={{ background: T.card, border: `1px solid ${T.border}` }}
         data-testid="sources-card">
      <CollapsibleHeader
        title="Chart Sources"
        subline={summary}
        collapsed={collapsed}
        onToggle={toggle}
        testid="sources-toggle"
      />
      {!collapsed && (
        <div className="mt-3 flex flex-col gap-2">
          {sources.map((s) => (
            <SourceRow
              key={s.id}
              source={s}
              isOn={(enabled || []).includes(s.id)}
              onToggle={() => onToggle(s.id)}
              disabled={saving}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SOURCE_BADGES = {
  official:            { label: "Official",            color: T.up,   bg: "rgba(61,220,151,0.1)" },
  licensing_required:  { label: "Licensing required",  color: T.lock, bg: T.lockTint },
  not_available:       { label: "Not available",       color: T.down, bg: "rgba(239,90,111,0.1)" },
  not_connected:       { label: "Not connected",       color: T.lock, bg: T.lockTint },
};

const SourceRow = ({ source, isOn, onToggle, disabled }) => {
  const badge = SOURCE_BADGES[source.type] || SOURCE_BADGES.official;
  const locked = source.locked;
  return (
    <div
      data-testid={`source-row-${source.id}`}
      className="flex items-start justify-between gap-3 rounded-lg p-3 transition"
      style={{
        background: locked ? "rgba(245,166,35,0.04)" : T.bg,
        border: `1px solid ${locked ? "rgba(245,166,35,0.2)" : T.border}`,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-medium" style={{ color: T.text }}>{source.label}</div>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
                style={{ background: badge.bg, color: badge.color }}>
            {locked && <Lock className="h-2.5 w-2.5" />}
            {badge.label}
          </span>
        </div>
        <div className="text-xs mt-1" style={{ color: T.muted }}>{source.sublabel}</div>
      </div>
      <ToggleSwitch on={isOn} locked={locked} onClick={onToggle} disabled={disabled}
                    testid={`source-toggle-${source.id}`} />
    </div>
  );
};

const ToggleSwitch = ({ on, locked, onClick, disabled, testid }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    data-testid={testid}
    aria-pressed={on}
    disabled={disabled}
    className="relative inline-flex items-center rounded-full transition-colors flex-shrink-0"
    style={{
      width: 34, height: 20,
      background: locked ? "rgba(245,166,35,0.35)" : on ? T.accent : "#3a3f4d",
      cursor: locked ? "not-allowed" : disabled ? "wait" : "pointer",
      opacity: locked ? 0.6 : 1,
    }}
  >
    <span className="inline-block rounded-full bg-white transition-transform"
          style={{
            width: 14, height: 14, marginLeft: 3,
            transform: `translateX(${on && !locked ? 14 : 0}px)`,
          }} />
  </button>
);

// ── Premium data placeholder card ───────────────────────────────────
const PremiumDataCard = ({ sources }) => {
  const [collapsed, toggle] = useCollapsible("topcharts.premium.collapsed", true);
  if (!sources || sources.length === 0) return null;
  const summary = collapsed
    ? `${sources.length} on the roadmap`
    : "Aggregator APIs for deeper coverage down the road";
  return (
    <div className="rounded-xl p-4" style={{ background: T.card, border: `1px solid ${T.border}` }}
         data-testid="premium-data-card">
      <CollapsibleHeader
        title="Premium Data (future)"
        subline={summary}
        collapsed={collapsed}
        onToggle={toggle}
        testid="premium-toggle"
      />
      {!collapsed && (
        <div className="mt-3 flex flex-col gap-2">
          {sources.map((s) => (
            <SourceRow
              key={s.id}
              source={s}
              isOn={false}
              onToggle={() => {
                toast.info("Premium data is on the roadmap — evaluating Chartmetric / Soundcharts pricing.");
              }}
              disabled={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Compliance banner ───────────────────────────────────────────────
const ComplianceBanner = () => (
  <div className="rounded-lg p-3 text-xs flex gap-2"
       style={{ background: T.lockTint, border: `1px solid rgba(245,166,35,0.3)`, color: T.text }}
       data-testid="compliance-banner">
    <Info className="h-4 w-4 flex-shrink-0" style={{ color: T.lock, marginTop: 1 }} />
    <div style={{ lineHeight: 1.5 }}>
      <strong style={{ color: T.lock }}>Licensed sources unavailable.</strong>{" "}
      <span style={{ color: T.muted }}>
        OCC (UK) and Billboard aren&apos;t wired up — they require direct B2B data-licensing
        agreements with each chart provider. Reach out to their teams to enable those toggles.
      </span>
    </div>
  </div>
);

// ── View tabs ───────────────────────────────────────────────────────
const ViewTabs = ({ view, setView, enabled }) => {
  const tabs = [
    { id: "blended",    label: "Blended",     always: true },
    { id: "applemusic", label: "Apple Music", needs: "applemusic" },
    { id: "lastfm",     label: "Last.fm",     needs: "lastfm" },
    { id: "deezer",     label: "Deezer",      needs: "deezer" },
  ];
  return (
    <div className="flex gap-1 rounded-lg p-1 mb-4 self-start"
         style={{ background: T.card, border: `1px solid ${T.border}` }}
         data-testid="view-tabs">
      {tabs.map((t) => {
        const disabled = !t.always && !(enabled || []).includes(t.needs);
        const active = view === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => !disabled && setView(t.id)}
            disabled={disabled}
            data-testid={`view-tab-${t.id}`}
            className="text-xs font-medium px-3 py-1.5 rounded-md transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: active ? T.accentTint : "transparent",
              color:      active ? T.accent : T.muted,
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
};

// ── Warnings ───────────────────────────────────────────────────────
const WarningStack = ({ warnings }) => (
  <div className="mb-3 flex flex-col gap-1" data-testid="chart-warnings">
    {warnings.map((w, i) => (
      <div key={i} className="text-xs rounded-md px-3 py-2"
           style={{ background: "rgba(245,166,35,0.08)", border: `1px solid rgba(245,166,35,0.25)`, color: T.lock }}>
        {w}
      </div>
    ))}
  </div>
);

// ── Chart list ─────────────────────────────────────────────────────
const ChartList = ({ entries, view, loading, onSendToShowPrep }) => {
  if (loading) {
    return (
      <div className="rounded-xl p-16 text-center"
           style={{ background: T.card, border: `1px solid ${T.border}`, color: T.muted }}
           data-testid="chart-list-loading">
        Loading chart data…
      </div>
    );
  }
  if (!entries || entries.length === 0) {
    return (
      <div className="rounded-xl p-16 text-center"
           style={{ background: T.card, border: `1px solid ${T.border}` }}
           data-testid="chart-list-empty">
        <Music className="h-12 w-12 mx-auto mb-3" style={{ color: T.faint }} />
        <div className="text-sm font-medium" style={{ color: T.text }}>No chart data yet</div>
        <div className="text-xs mt-1" style={{ color: T.muted }}>
          Enable at least one sanctioned source, or click Refresh to fetch a fresh snapshot.
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl overflow-hidden"
         style={{ background: T.card, border: `1px solid ${T.border}` }}
         data-testid="chart-list">
      {entries.map((e) => (
        <ChartRow key={e.entry_id || `${e.source}-${e.rank}-${e.title}`}
                  entry={e} view={view} onSendToShowPrep={onSendToShowPrep} />
      ))}
    </div>
  );
};

const ChartRow = ({ entry, view, onSendToShowPrep }) => {
  const rank = view === "blended" ? (entry.blended_rank || entry.rank) : entry.rank;
  const sources = entry.sources || [entry.source];
  const sourceLabel = view === "blended"
    ? _blendedSourceLabel(sources)
    : (entry.source === "applemusic" ? "Apple Music"
       : entry.source === "lastfm" ? "Last.fm"
       : entry.source === "deezer" ? "Deezer"
       : entry.source);

  return (
    <div className="flex items-center gap-4 px-4 py-3 transition"
         style={{ borderTop: `1px solid ${T.divider}` }}
         data-testid={`chart-row-${rank}`}
         onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
         onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
      {/* Rank */}
      <div className="text-lg font-semibold flex-shrink-0"
           style={{ color: T.text, width: 32, textAlign: "center" }}>
        {rank}
      </div>
      {/* Album art */}
      <div className="flex-shrink-0 rounded-md overflow-hidden"
           style={{ width: 44, height: 44, background: T.bg,
                    border: `1px solid ${T.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
        {entry.artwork_url
          ? <img src={entry.artwork_url} alt="" style={{ width: 44, height: 44, objectFit: "cover" }} />
          : <Music className="h-5 w-5" style={{ color: T.faint }} />}
      </div>
      {/* Title/artist */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="text-sm font-medium truncate" style={{ color: T.text }}
             title={entry.title}>{entry.title}</div>
        <div className="text-xs truncate" style={{ color: T.muted }}
             title={entry.artist}>{entry.artist}</div>
      </div>
      {/* Source tag */}
      <div className="text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded flex-shrink-0"
           style={{ color: T.accent, background: T.accentTint }}>
        {sourceLabel}
      </div>
      {/* Movement */}
      <MovementBadge movement={entry.movement} />
      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <IconButton icon={Pencil} onClick={() => onSendToShowPrep(entry)}
                    title="Send to Show Prep"
                    testid={`chart-send-showprep-${rank}`} />
        {entry.external_url && (
          <IconButton icon={ExternalLink}
                      onClick={() => window.open(entry.external_url, "_blank", "noopener")}
                      title="Open source"
                      testid={`chart-open-source-${rank}`} />
        )}
      </div>
    </div>
  );
};

// ── Blended source label helper ─────────────────────────────────────
// Renders "Apple + Last.fm" / "Apple + Deezer" / "Apple + Last.fm + Deezer"
// etc. based on which sources contributed to this blended entry. Falls
// back to the single-source name when only one source has this track.
function _blendedSourceLabel(sources) {
  const map = { applemusic: "Apple", lastfm: "Last.fm", deezer: "Deezer" };
  const named = (sources || []).map((s) => map[s]).filter(Boolean);
  if (named.length === 0) return "—";
  if (named.length === 1) return named[0] === "Apple" ? "Apple Music" : named[0];
  return named.join(" + ");
}

const MovementBadge = ({ movement }) => {
  const m = movement || { direction: "flat", delta: 0 };
  if (m.direction === "up") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium flex-shrink-0"
            style={{ color: T.up, width: 52 }}>
        <TrendingUp className="h-3.5 w-3.5" />{m.delta}
      </span>
    );
  }
  if (m.direction === "down") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium flex-shrink-0"
            style={{ color: T.down, width: 52 }}>
        <TrendingDown className="h-3.5 w-3.5" />{m.delta}
      </span>
    );
  }
  if (m.direction === "new") {
    return (
      <span className="inline-flex items-center text-[10px] font-semibold uppercase flex-shrink-0"
            style={{ color: T.accent, width: 52 }}>
        NEW
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs flex-shrink-0"
          style={{ color: T.faint, width: 52 }}>
      <Minus className="h-3.5 w-3.5" />
    </span>
  );
};

const IconButton = ({ icon: Icon, onClick, title, testid }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    data-testid={testid}
    className="rounded-md p-1.5 transition"
    style={{ color: T.muted, background: "transparent" }}
    onMouseEnter={(e) => {
      e.currentTarget.style.color = T.accent;
      e.currentTarget.style.background = T.accentTint;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.color = T.muted;
      e.currentTarget.style.background = "transparent";
    }}
  >
    <Icon className="h-4 w-4" />
  </button>
);

// ── Add-region modal ────────────────────────────────────────────────
const AddRegionModal = ({ available, onPick, onClose }) => {
  const [query, setQuery] = useState("");
  const filtered = available.filter((r) =>
    r.name.toLowerCase().includes(query.toLowerCase())
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="add-region-modal">
      <div className="w-full max-w-md rounded-xl overflow-hidden"
           style={{ background: T.card, border: `1px solid ${T.border}` }}
           onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b" style={{ borderColor: T.divider }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold" style={{ color: T.text }}>Add a region</h3>
            <button onClick={onClose} data-testid="add-region-close"
                    className="text-lg leading-none" style={{ color: T.muted }}>×</button>
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search regions…"
            autoFocus
            data-testid="add-region-search"
            className="w-full rounded-md px-3 py-2 text-sm focus:outline-none"
            style={{ background: T.bg, border: `1px solid ${T.border}`, color: T.text }}
          />
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          {filtered.length === 0 && (
            <div className="text-center py-8 text-xs" style={{ color: T.muted }}>
              No regions match &ldquo;{query}&rdquo;
            </div>
          )}
          {filtered.map((r) => (
            <button
              key={r.code}
              type="button"
              onClick={() => onPick(r.code)}
              data-testid={`add-region-option-${r.code}`}
              className="flex items-center gap-3 w-full text-left px-3 py-2 rounded-md text-sm transition"
              style={{ color: T.text }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.bg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ fontSize: 16 }}>{r.flag}</span>
              {r.name}
              <span className="ml-auto text-xs" style={{ color: T.faint }}>{r.code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
